import { useEffect, useRef, useState } from 'react';

/** Options temporelles de useLoadingState (toutes en millisecondes). */
export interface UseLoadingStateOptions {
  /**
   * Delai avant d'afficher l'etat de chargement. Si la donnee arrive avant,
   * rien ne s'affiche du tout (pas de flash de skeleton). Defaut 300.
   */
  delayMs?: number;
  /**
   * Duree minimale d'affichage une fois l'etat montre : il reste visible au
   * moins ce temps meme si la donnee arrive entre-temps (pas de clignotement).
   * Defaut 400.
   */
  minVisibleMs?: number;
  /**
   * Au-dela de cette duree de chargement continu, `timedOut` passe a true
   * (l'appelant affiche alors un etat d'erreur / de relance). Defaut 10000.
   */
  timeoutMs?: number;
}

/** Resultat de useLoadingState. */
export interface LoadingStateResult {
  /** Afficher l'etat de chargement (skeleton) maintenant. */
  showLoading: boolean;
  /** Le chargement dure anormalement longtemps (au-dela de timeoutMs). */
  timedOut: boolean;
}

/**
 * Transforme un booleen `loading` brut en etat d'affichage poli :
 *
 * 1. DELAI : l'affichage n'apparait qu'apres `delayMs` de chargement continu.
 *    Donnee rapide = aucun skeleton, l'ecran ne flashe pas.
 * 2. DUREE MINIMALE : une fois affiche, l'etat reste visible au moins
 *    `minVisibleMs`. Pas de clignotement quand la donnee arrive juste apres.
 * 3. TIMEOUT : apres `timeoutMs` de chargement continu, `timedOut` passe a
 *    true pour basculer vers un etat d'erreur explicite.
 *
 * Tous les timers sont nettoyes (unmount, alternances rapides de `loading`).
 * Une reprise de chargement pendant la fenetre de duree minimale garde le
 * skeleton affiche sans le redemarrer (pas de flicker).
 */
export function useLoadingState(
  loading: boolean,
  opts: UseLoadingStateOptions = {},
): LoadingStateResult {
  const { delayMs = 300, minVisibleMs = 400, timeoutMs = 10000 } = opts;

  const [showLoading, setShowLoading] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  // Horodatage du moment ou le skeleton est devenu visible (null = pas visible).
  // Ref et pas state : lu par les timers sans redeclencher l'effet.
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (loading) {
      setTimedOut(false);
      const timers: ReturnType<typeof setTimeout>[] = [];
      // Regle 1 : n'armer le delai que si le skeleton n'est pas deja visible
      // (une alternance rapide false -> true pendant la fenetre de duree
      // minimale garde l'affichage en cours, sans repartir de zero).
      if (shownAtRef.current == null) {
        timers.push(
          setTimeout(() => {
            shownAtRef.current = Date.now();
            setShowLoading(true);
          }, delayMs),
        );
      }
      // Regle 3 : timeout du chargement continu.
      timers.push(setTimeout(() => setTimedOut(true), timeoutMs));
      return () => {
        for (const timer of timers) clearTimeout(timer);
      };
    }

    // Chargement termine.
    setTimedOut(false);
    if (shownAtRef.current == null) {
      // Jamais affiche : le timer de delai a ete nettoye par le cleanup
      // ci-dessus, il n'y a rien a masquer.
      setShowLoading(false);
      return undefined;
    }
    // Regle 2 : respecter la duree minimale de visibilite avant de masquer.
    const remaining = minVisibleMs - (Date.now() - shownAtRef.current);
    if (remaining <= 0) {
      shownAtRef.current = null;
      setShowLoading(false);
      return undefined;
    }
    const hideTimer = setTimeout(() => {
      shownAtRef.current = null;
      setShowLoading(false);
    }, remaining);
    return () => clearTimeout(hideTimer);
  }, [loading, delayMs, minVisibleMs, timeoutMs]);

  return { showLoading, timedOut };
}
